const COMMAND_TIMEOUT_MS = 40_000; // 40 seconds
const PING_TIMEOUT_MS = 60_000;    // 60 seconds for readiness ping (WASM cold-start)
const PING_RETRY_INTERVAL_MS = 2_000; // Retry PING every 2 seconds until engine responds

class PdfEngineClient {
  constructor(engineUrl) {
    this.engineUrl = engineUrl;
    this.iframe = null;
    this.pendingMap = new Map(); // correlationId → { resolve, reject, timeoutId }
    this._ready = false;
    this._readyPromise = null;
    this._cancelReady = null; // called by dispose() to abort _waitForReady
    this._messageHandler = this._onMessage.bind(this);

    this._initIframe();
  }

  // ─── Initialisation ────────────────────────────────────────────────────────

  _initIframe() {
    this.iframe = document.createElement("iframe");
    this.iframe.src = this.engineUrl;
    this.iframe.style.display = "none";
    document.body.appendChild(this.iframe);

    window.addEventListener("message", this._messageHandler);

    // Start readiness handshake immediately; any sendCommand call awaits this.
    this._readyPromise = this._waitForReady();
  }

  /**
   * Waits for the engine to respond to a PING.
   * Retries every PING_RETRY_INTERVAL_MS until PING_TIMEOUT_MS is exceeded.
   * Blazor WASM has a cold-start delay (downloading + compiling), so retries
   * are necessary — the first few PINGs will be ignored by the engine host
   * because DotNet isn't initialised yet.
   * @returns {Promise<void>}
   */
  _waitForReady() {
    return new Promise((resolve, reject) => {
      let resolved = false;

      const finish = (ok, err) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(deadlineId);
        clearInterval(retryId);
        this._cancelReady = null;
        if (ok) { this._ready = true; resolve(); }
        else { reject(err); }
      };

      // Expose a cancel hook so dispose() can abort the handshake cleanly.
      this._cancelReady = () => {
        const err = new Error("PDF Engine client disposed");
        err.code = "ENGINE_DISPOSED";
        finish(false, err);
      };

      // Hard deadline: give up after PING_TIMEOUT_MS total
      const deadlineId = setTimeout(() => {
        finish(false, new Error("PDF Engine did not respond to PING within timeout"));
      }, PING_TIMEOUT_MS);

      const sendPing = () => {
        if (resolved) return;
        const correlationId = this._generateId();

        // Each PING attempt gets its own entry so _onMessage can resolve it.
        // reject is a no-op — the deadline handles overall failure.
        this.pendingMap.set(correlationId, {
          resolve: () => finish(true),
          reject: () => {},
          timeoutId: null, // no per-ping timeout — deadline covers it
        });

        if (this.iframe?.contentWindow) {
          this.iframe.contentWindow.postMessage(
            { type: "PING", correlationId },
            "*",
          );
        }
      };

      // First attempt after a short delay (allow iframe src to start loading),
      // then retry on interval until the engine wakes up.
      const retryId = setInterval(sendPing, PING_RETRY_INTERVAL_MS);
      setTimeout(sendPing, 500);
    });
  }

  // ─── Message Listener ──────────────────────────────────────────────────────

  _onMessage(event) {
    // Security: only accept messages from our own iframe
    if (event.source !== this.iframe?.contentWindow) return;

    const data = event.data;
    if (!data || !data.correlationId || !data.result) return;
    // Optional type guard: engine response types end with "_RESULT"
    if (typeof data.type === "string" && !data.type.endsWith("_RESULT")) return;

    const entry = this.pendingMap.get(data.correlationId);
    if (!entry) return;

    clearTimeout(entry.timeoutId);
    this.pendingMap.delete(data.correlationId);

    const { result } = data;

    // PING responses may be a plain string (engine is alive but doesn't wrap in EngineResponse).
    // For readiness, any response means the engine is up — resolve regardless of shape.
    if (data.type === "PING_RESULT") {
      entry.resolve(result);
      return;
    }

    if (result.success === true) {
      entry.resolve(result);
    } else {
      const firstError = result.errors?.[0];
      const err = new Error(
        firstError?.message || result.message || "PDF Engine command failed",
      );
      err.code = firstError?.code || "ENGINE_ERROR";
      entry.reject(err);
    }
  }

  // ─── Core Send ─────────────────────────────────────────────────────────────

  /**
   * Sends a command to the engine iframe and returns a Promise.
   * Automatically awaits readiness before the first real command.
   *
   * @param {string} type - Command type (e.g. "ENCRYPT_PDF")
   * @param {object} payload - Additional fields merged into the message
   * @returns {Promise<object>} Resolves with result object on success
   */
  async sendCommand(type, payload = {}) {
    // Await readiness (no-op after first success)
    if (!this._ready) {
      await this._readyPromise;
    }

    if (!this.iframe?.contentWindow) {
      throw new Error("PDF Engine iframe not available");
    }

    return new Promise((resolve, reject) => {
      const correlationId = this._generateId();

      const timeoutId = setTimeout(() => {
        this.pendingMap.delete(correlationId);
        const err = new Error(
          `PDF Engine command "${type}" timed out after ${COMMAND_TIMEOUT_MS / 1000}s`,
        );
        err.code = "ENGINE_TIMEOUT";
        reject(err);
      }, COMMAND_TIMEOUT_MS);

      this.pendingMap.set(correlationId, { resolve, reject, timeoutId });

      this.iframe.contentWindow.postMessage(
        { type, correlationId, ...payload },
        "*",
      );
    });
  }

  _generateId() {
    return window.crypto.randomUUID();
  }

  // ─── Dispose ───────────────────────────────────────────────────────────────

  /**
   * Tears down the client: removes listener, removes iframe, rejects pending.
   */
  dispose() {
    window.removeEventListener("message", this._messageHandler);

    // Abort the readiness handshake if still in progress
    this._cancelReady?.();

    for (const [, entry] of this.pendingMap) {
      clearTimeout(entry.timeoutId);
      const err = new Error("PDF Engine client disposed");
      err.code = "ENGINE_DISPOSED";
      entry.reject(err);
    }
    this.pendingMap.clear();

    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
    }
    this.iframe = null;
    this._ready = false;
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  // Step 0: Ping (used internally for readiness; also exposed for manual testing)
  ping() {
    return this.sendCommand("PING");
  }

  // Step 1: Create a test PDF
  createTestPdf() {
    return this.sendCommand("CREATE_TEST_PDF");
  }

  // Step 2: Check if PDF is encrypted
  isPdfEncrypted(pdfBase64) {
    return this.sendCommand("IS_ENCRYPTED", { pdfBase64 });
  }

  // Step 3: Encrypt PDF with password
  encryptPdf(pdfBase64, password) {
    return this.sendCommand("ENCRYPT_PDF", { pdfBase64, password });
  }

  // Step 4: Embed attachments into PDF
  embedAttachments(pdfBase64, attachments) {
    return this.sendCommand("EMBED_ATTACHMENTS", { pdfBase64, attachments });
  }

  // TODO [React]: Validate attachments with isBlockedAttachment() from blockedextensions-helper.js before calling WASM.
  // TODO [React]: Map ATTACHMENT_BLOCKED_EXTENSION errors to toast messages via getErrorMessage() from error-helper.js.

  // TODO [React]:
  // When encrypting multiple PDF attachments in a loop:
  // - If EncryptPdf returns error code PDF_ALREADY_ENCRYPTED,
  //   do NOT treat it as a fatal error.
  // - Skip this attachment and continue encrypting the remaining PDFs.
  // - Collect skipped items and show a single informational message:
  //   "Ein PDF-Anhang dieser E-Mail ist bereits passwortgeschützt und wird nicht erneut verschlüsselt."
  // - Display it as an Information dialog/toast (not error).
  // - Ensure the overall process still succeeds if at least one PDF was processed successfully.

  // Step 5: Build container PDF, embed attachments, encrypt
  buildContainerAndEncrypt({ meta, bodyText, attachments, emlBase64, password, isPaid = false }) {
    return this.sendCommand("BUILD_CONTAINER_AND_ENCRYPT", {
      meta,
      bodyText,
      attachments,
      emlBase64,
      password,
      isPaid,
    });
  }
}

export default PdfEngineClient;
